import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AdminFeedbackResponse, ReviewStatus } from '@fois-utsavvoice/shared';
import { getResponse, getResponsePhotoUrl, updateResponse } from '../api/admin.js';
import { useAuth } from '../auth/AuthContext.js';
import { AdminNav } from '../components/AdminNav.js';
import { PageShell } from '../components/PageShell.js';

export function AdminResponseDetailPage() {
  const { responseId } = useParams<{ responseId: string }>();
  const auth = useAuth();
  const [response, setResponse] = useState<AdminFeedbackResponse | undefined>();
  const [notes, setNotes] = useState('');
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus>('NEW');
  const [photoUrl, setPhotoUrl] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!auth.tokens || !responseId) {
      return;
    }
    getResponse(auth.tokens.idToken, responseId)
      .then((item) => {
        setResponse(item);
        setNotes(item.adminNotes ?? '');
        setReviewStatus(item.reviewStatus);
        if (item.hasPhoto) {
          return getResponsePhotoUrl(auth.tokens!.idToken, responseId).then((grant) =>
            setPhotoUrl(grant.url),
          );
        }
        return undefined;
      })
      .catch(() => setError('The response could not be loaded.'));
  }, [auth.tokens, responseId]);

  async function onSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!auth.tokens || !responseId) {
      return;
    }
    setSaving(true);
    try {
      const updated = await updateResponse(auth.tokens.idToken, responseId, {
        adminNotes: notes,
        reviewStatus,
      });
      setResponse(updated);
    } catch {
      setError('The review could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  async function onApprove(approved: boolean): Promise<void> {
    if (!auth.tokens || !responseId || !response) {
      return;
    }
    if (approved && response.consent === 'PRIVATE') {
      setError('Private feedback cannot be approved as a testimonial.');
      return;
    }
    setSaving(true);
    try {
      setResponse(
        await updateResponse(auth.tokens.idToken, responseId, {
          testimonialApproved: approved,
        }),
      );
    } catch {
      setError('Testimonial approval could not be updated.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageShell>
      <AdminNav />
      <section className="card mx-auto max-w-3xl">
        <p>
          <Link className="font-semibold text-crimson underline" to="/admin">
            Back to dashboard
          </Link>
        </p>
        <h1 className="mt-4 heading-xl text-crimson">Response detail</h1>
        {error ? (
          <p className="mt-3 text-sm font-semibold text-crimson" role="alert">
            {error}
          </p>
        ) : null}
        {response ? (
          <>
            <dl className="mt-6 grid gap-3 sm:grid-cols-2">
              <Item label="Product" value={response.productName} />
              <Item label="Order" value={response.orderReference} />
              <Item label="Overall" value={response.overallRating} />
              <Item label="Product selection" value={response.productRating} />
              <Item label="Packaging" value={response.packagingRating} />
              <Item label="Value" value={response.valueRating} />
              <Item label="Delivery" value={response.deliveryRating ?? 'Not applicable'} />
              <Item label="Recommend" value={response.recommendation} />
              <Item label="Buy again" value={response.purchaseAgain} />
              <Item label="Consent" value={response.consent} />
              <Item label="Display name" value={response.displayName ?? '—'} />
              <Item label="Submitted" value={new Date(response.submittedAt).toLocaleString()} />
            </dl>
            <p className="mt-4">
              <strong>Liked most:</strong> {response.likedMost || '—'}
            </p>
            <p className="mt-2">
              <strong>Improvement:</strong> {response.improvement || '—'}
            </p>
            {photoUrl ? (
              <figure className="mt-6">
                <img
                  className="max-h-80 rounded-none border border-linen"
                  src={photoUrl}
                  alt="Customer photo"
                />
              </figure>
            ) : null}

            <form className="mt-8 space-y-4" onSubmit={onSave}>
              <div>
                <label className="field-label" htmlFor="reviewStatus">
                  Review status
                </label>
                <select
                  id="reviewStatus"
                  className="field-input"
                  value={reviewStatus}
                  onChange={(event) => setReviewStatus(event.target.value as ReviewStatus)}
                >
                  <option value="NEW">New</option>
                  <option value="REVIEWED">Reviewed</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="adminNotes">
                  Internal notes
                </label>
                <textarea
                  id="adminNotes"
                  className="field-input"
                  maxLength={2000}
                  rows={4}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </div>
              <button className="btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save review'}
              </button>
            </form>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                className="btn-secondary"
                type="button"
                disabled={saving || response.consent === 'PRIVATE'}
                onClick={() => onApprove(true)}
              >
                {response.testimonialApproved ? 'Approved for testimonial' : 'Approve testimonial'}
              </button>
              {response.testimonialApproved ? (
                <button
                  className="btn-secondary"
                  type="button"
                  disabled={saving}
                  onClick={() => onApprove(false)}
                >
                  Clear approval
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <p className="mt-4" role="status">
            Loading response…
          </p>
        )}
      </section>
    </PageShell>
  );
}

function Item({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <dt className="text-sm font-semibold text-charcoal/70">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
